import {
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  HomeIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FC, useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const SidebarContainer = styled.aside<{ isOpen: boolean }>`
  width: 250px;
  background: white;
  border-right: 1px solid var(--gray-200);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 50;
    transform: ${(props) =>
      props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SidebarHeader = styled.div`
  padding: 0 1.5rem 8px 1.5rem;
  border-bottom: 1px solid var(--gray-200);
  background: var(--primary-600);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 3.5rem;
  justify-content: center;

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    margin-top: 0.5rem;
  }

  p {
    font-size: 0.75rem;
    opacity: 0.9;
    margin: 2px 0 3px 0;
  }
`;

const SidebarNav = styled.nav`
  padding: 1rem 0;
  flex: 1;
  overflow-y: auto;
`;

const SidebarGroup = styled.div`
  margin-bottom: 1rem;

  .group-title {
    padding: 0.5rem 1.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--gray-500);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s;
    border-bottom: 2px solid var(--primary-600);
    background: var(--gray-100);

    &:hover {
      color: var(--gray-700);
      background-color: var(--gray-50);
    }

    svg {
      width: 1rem;
      height: 1rem;
      transition: transform 0.2s;
    }
  }

  .group-items {
    overflow: hidden;
    transition:
      max-height 0.3s ease-in-out,
      opacity 0.3s ease-in-out;

    &.collapsed {
      max-height: 0;
      opacity: 0;
    }

    &.expanded {
      max-height: 2000px;
      opacity: 1;
    }
  }
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: var(--gray-700);
  text-decoration: none;
  transition: all 0.2s;
  font-size: 0.875rem;
  font-weight: 500;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  &:hover {
    background-color: var(--gray-50);
    color: var(--primary-600);
  }

  &.active {
    background-color: var(--primary-50);
    color: var(--primary-600);
    border-left: 3px solid var(--primary-600);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const Sidebar: FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >(() => {
    const saved = localStorage.getItem('collapsedMenuGroups');
    return saved
      ? JSON.parse(saved)
      : {
          main: false,
          commerce: false,
          system: false,
        };
  });

  const toggleGroup = (groupKey: string) => {
    const newCollapsed = {
      ...collapsedGroups,
      [groupKey]: !collapsedGroups[groupKey],
    };
    setCollapsedGroups(newCollapsed);
    localStorage.setItem('collapsedMenuGroups', JSON.stringify(newCollapsed));
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <SidebarContainer isOpen={sidebarOpen}>
      <SidebarHeader>
        <CloseButton onClick={closeSidebar}>
          <XMarkIcon />
        </CloseButton>
        <h1>MARKETEER</h1>
        <p>Commerce Platform</p>
      </SidebarHeader>

      <SidebarNav>
        <SidebarGroup>
          <div className="group-title" onClick={() => toggleGroup('main')}>
            <span>Main</span>
            {collapsedGroups.main ? <ChevronRightIcon /> : <ChevronDownIcon />}
          </div>
          <div
            className={`group-items ${collapsedGroups.main ? 'collapsed' : 'expanded'}`}
          >
            <NavItem to="/dashboard" onClick={closeSidebar}>
              <HomeIcon />
              Dashboard
            </NavItem>
          </div>
        </SidebarGroup>

        <SidebarGroup>
          <div className="group-title" onClick={() => toggleGroup('commerce')}>
            <span>E-Commerce</span>
            {collapsedGroups.commerce ? (
              <ChevronRightIcon />
            ) : (
              <ChevronDownIcon />
            )}
          </div>
          <div
            className={`group-items ${collapsedGroups.commerce ? 'collapsed' : 'expanded'}`}
          >
            <NavItem to="/products" onClick={closeSidebar}>
              <ShoppingBagIcon />
              Products
            </NavItem>

            <NavItem to="/orders" onClick={closeSidebar}>
              <ShoppingCartIcon />
              Orders
            </NavItem>

            <NavItem to="/warehouses" onClick={closeSidebar}>
              <BuildingOfficeIcon />
              Warehouses
            </NavItem>

            <NavItem to="/marketplaces" onClick={closeSidebar}>
              <GlobeAltIcon />
              Marketplaces
            </NavItem>

            <NavItem to="/analytics" onClick={closeSidebar}>
              <ChartBarIcon />
              Analytics
            </NavItem>
          </div>
        </SidebarGroup>

        <SidebarGroup>
          <div className="group-title" onClick={() => toggleGroup('system')}>
            <span>System</span>
            {collapsedGroups.system ? (
              <ChevronRightIcon />
            ) : (
              <ChevronDownIcon />
            )}
          </div>
          <div
            className={`group-items ${collapsedGroups.system ? 'collapsed' : 'expanded'}`}
          >
            <NavItem to="/tenants" onClick={closeSidebar}>
              <BuildingStorefrontIcon />
              Tenants
            </NavItem>

            <NavItem to="/users" onClick={closeSidebar}>
              <UserGroupIcon />
              Users
            </NavItem>

            <NavItem to="/settings" onClick={closeSidebar}>
              <Cog6ToothIcon />
              Settings
            </NavItem>
          </div>
        </SidebarGroup>
      </SidebarNav>
    </SidebarContainer>
  );
};

export default Sidebar;
